import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setCurrentConversation, setMessages } from '../store/reducers/messages.reducer';
import { useMessaging } from '../hooks/useMessaging';
import { ChatWindow } from '../components/chat/ChatWindow';
import { InAppCamera } from '../components/chat/InAppCamera';
import { getFamilyMembers } from '../api/userApi';
import { User } from '../types';
import { compressMessageImage } from '../utils/imageCompression';
import { encryptMedia } from '../utils/mediaEncryption';

export const MessagesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loginUser } = useAppSelector((state) => state.user);
  const { currentConversation, onlineUsers } = useAppSelector((state) => state.messages);
  const { conversations, messages, sendMessage, editMessage, deleteMessage, loadMessages, markAsRead, isUserOnline, isConnected, deleteConversation, provider } = useMessaging();
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [familyLoaded, setFamilyLoaded] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

  const handleCameraCapture = async (blob: Blob) => {
    setShowCamera(false);
    if (!currentConversation) return;
    try {
      const filename = `photo-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      const compressed = await compressMessageImage(file);
      const { encryptedBlob, key, mimeType } = await encryptMedia(compressed);
      const formData = new FormData();
      formData.append('media', encryptedBlob, filename);
      const res = await fetch(`${API_BASE_URL}/api/messages/upload-media`, { method: 'POST', body: formData, credentials: 'include' });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await sendMessage(currentConversation, '', { ...data.data, mediaKey: key, mediaMimeType: mimeType });
    } catch {
      alert('Failed to send photo. Please try again.');
    }
  };
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [swipedConversation, setSwipedConversation] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Track visual viewport height only for Android keyboard-open adjustment.
  // Default is null — CSS handles it via calc(100vh - 64px).
  const [keyboardHeight, setKeyboardHeight] = useState<number | null>(null);

  useEffect(() => {
    const update = () => {
      const vvh = window.visualViewport?.height;
      // Only override CSS height when keyboard shrinks the viewport significantly
      if (vvh && vvh < window.innerHeight - 100) {
        setKeyboardHeight(vvh - 64);
      } else {
        setKeyboardHeight(null);
      }
    };
    window.visualViewport?.addEventListener('resize', update);
    return () => window.visualViewport?.removeEventListener('resize', update);
  }, []);

  // Convert onlineUsers array to object for easy lookup
  const onlineStatus = React.useMemo(() => {
    const status: { [key: string]: boolean } = {};
    onlineUsers.forEach(userId => { status[userId] = true; });
    return status;
  }, [onlineUsers]);

  useEffect(() => {
    const FAMILY_CACHE_KEY = 'family_members_cache';
    const FAMILY_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

    const loadFamily = async () => {
      // Serve from cache immediately if fresh — no loading wait
      try {
        const cached = sessionStorage.getItem(FAMILY_CACHE_KEY);
        if (cached) {
          const { members, ts } = JSON.parse(cached);
          if (Date.now() - ts < FAMILY_CACHE_TTL && members?.length > 0) {
            setFamilyMembers(members);
            setFamilyLoaded(true);
            return; // cache hit — skip network call
          }
        }
      } catch { /* ignore parse errors */ }

      try {
        const response = await getFamilyMembers();
        if (response.data?.success && response.data.data?.members) {
          const members = response.data.data.members;
          setFamilyMembers(members);
          // Cache for next visit (only metadata, no message content)
          sessionStorage.setItem(FAMILY_CACHE_KEY, JSON.stringify({ members, ts: Date.now() }));
        }
      } catch (error) {
        console.error('Failed to load family members:', error);
      } finally {
        setFamilyLoaded(true);
      }
    };

    loadFamily();
  }, []);

  // Load messages whenever the active conversation changes (covers call-navigation and page refresh)
  useEffect(() => {
    if (familyLoaded && currentConversation) {
      loadMessages(currentConversation);
      markAsRead(currentConversation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyLoaded, currentConversation]);

  // Real-time edit/delete from the other participant
  const messagesRef = React.useRef(messages);
  const convRef = React.useRef(currentConversation);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { convRef.current = currentConversation; }, [currentConversation]);

  useEffect(() => {
    const socket = (provider as any)?.getSocket?.();
    if (!socket) return;

    const onEdited = ({ messageId, newContent, editedAt }: { messageId: string; newContent: string; editedAt: number }) => {
      const conv = convRef.current;
      if (!conv) return;
      dispatch(setMessages({
        userId: conv,
        messages: (messagesRef.current || []).map(m =>
          m.id === messageId ? { ...m, content: newContent, editedAt } : m
        )
      }));
    };

    const onDeleted = ({ messageId }: { messageId: string }) => {
      const conv = convRef.current;
      if (!conv) return;
      dispatch(setMessages({
        userId: conv,
        messages: (messagesRef.current || []).filter(m => m.id !== messageId)
      }));
    };

    socket.on('message-edited', onEdited);
    socket.on('message-deleted', onDeleted);
    return () => {
      socket.off('message-edited', onEdited);
      socket.off('message-deleted', onDeleted);
    };
  }, [provider, dispatch]);


  const handleSelectConversation = async (userId: string) => {
    dispatch(setCurrentConversation(userId));
    await loadMessages(userId);
    await markAsRead(userId);
  };

  const handleSendMessage = async (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string }) => {
    if (!currentConversation) return;
    await sendMessage(currentConversation, content, mediaData);
  };

  const handleCloseChat = () => {
    dispatch(setCurrentConversation(null));
  };

  const handleDeleteConversation = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Permanently delete this conversation? This cannot be undone.')) return;

    try {
      await deleteConversation(userId);
      setSwipedConversation(null);
      handleCloseChat();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handleTouchStart = (userId: string, e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setTouchEnd(null);
  };

  const handleTouchMove = (userId: string, e: React.TouchEvent) => {
    setTouchEnd({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (userId: string) => {
    if (!touchStart || !touchEnd) return;

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);

    // Swipe left to reveal delete (deltaX > 50 = swipe left, deltaY < 30 = horizontal swipe)
    if (deltaX > 50 && deltaY < 30) {
      setSwipedConversation(userId);
    } else if (deltaX < -50 && deltaY < 30) {
      // Swipe right to hide delete
      setSwipedConversation(null);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentRecipient = familyMembers.find(m => String(m.id) === currentConversation);

  return (
    <>
    <div className="messages-layout" style={{ height: keyboardHeight ?? 'calc(100dvh - 64px)' }}>
      {/* Conversations List */}
      <div className={`messages-list-pane${currentConversation ? ' pane-hidden' : ''}`}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <div className="card" style={{ flex: 1, borderRadius: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="card-content" style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="h5 mb-0">Messages</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  className="btn btn-sm btn-contained"
                  onClick={() => setShowNewChatModal(true)}
                  style={{ padding: '4px 8px' }}
                >
                  <span className="material-icons" style={{ fontSize: '18px' }}>add</span>
                </button>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: isConnected ? 'var(--success-color)' : 'var(--error-color)'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isConnected ? 'var(--success-color)' : 'var(--error-color)'
                  }} />
                  {isConnected ? 'Connected' : 'Offline'}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable conversations area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {conversations.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '32px' }}>
                <span className="material-icons" style={{ fontSize: '48px', opacity: 0.3 }}>
                  forum
                </span>
                <div style={{ marginTop: '8px' }}>No conversations yet</div>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  Open a family member's profile to start chatting
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {conversations.map((conv) => {
                  const member = familyMembers.find(m => String(m.id) === conv.userId);
                  if (!member) return null;

                  const displayName = member.displayName || member.display_name || member.email;
                  const avatarUrl = member.photoURL || member.photo_url;
                  const isOnline = onlineStatus[conv.userId] || false;

                  return (
                    <div
                      key={conv.userId}
                      style={{ position: 'relative', overflow: 'hidden' }}
                      onTouchStart={(e) => handleTouchStart(conv.userId, e)}
                      onTouchMove={(e) => handleTouchMove(conv.userId, e)}
                      onTouchEnd={() => handleTouchEnd(conv.userId)}
                    >
                      <div
                        className="card"
                        onClick={() => handleSelectConversation(conv.userId)}
                        style={{
                          cursor: 'pointer',
                          borderRadius: '9px',
                          border: currentConversation === conv.userId ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                          transform: swipedConversation === conv.userId ? 'translateX(-80px)' : 'translateX(0)',
                          transition: 'transform 0.3s ease',
                          position: 'relative'
                        }}
                      >
                        <div className="card-content" style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ position: 'relative' }}>
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={displayName}
                                  style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px rgba(255,255,255,0.7)' }}
                                />
                              ) : (
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '50%',
                                  background: 'var(--primary-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  boxShadow: '0 0 0 2px rgba(255,255,255,0.7)'
                                }}>
                                  {displayName.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {isOnline && (
                                <span style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  right: '2px',
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  background: 'var(--success-color)',
                                  border: '2px solid var(--background-paper)'
                                }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                {displayName}
                              </div>
                              {conv.lastMessage && (
                                <div style={{
                                  fontSize: '14px',
                                  color: 'var(--text-secondary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {conv.lastMessage.content}
                                </div>
                              )}
                            </div>
                            {/* Right column: unread badge + delete, stacked so they never overlap */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                              {conv.unreadCount > 0 && (
                                <div style={{
                                  background: 'var(--primary-color)',
                                  color: 'white',
                                  borderRadius: '50%',
                                  minWidth: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {conv.unreadCount}
                                </div>
                              )}
                              <button
                                className="icon-btn"
                                onClick={(e) => handleDeleteConversation(conv.userId, e)}
                                title="Delete conversation"
                                style={{ color: 'var(--error-color)', padding: '2px' }}
                              >
                                <span className="material-icons" style={{ fontSize: '18px' }}>delete</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Delete button revealed on swipe */}
                      {swipedConversation === conv.userId && (
                        <div
                          onClick={(e) => handleDeleteConversation(conv.userId, e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '80px',
                            background: 'var(--error-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          <span className="material-icons">delete</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Chat Window */}
      <div className={`messages-chat-pane${currentConversation ? ' pane-visible' : ''}`}>
        {currentConversation && currentRecipient ? (
          <ChatWindow
            recipient={currentRecipient}
            currentUserId={String(loginUser?.id)}
            messages={messages}
            onSendMessage={handleSendMessage}
            onEditMessage={(msgId, newText) => editMessage(msgId, currentConversation!, newText)}
            onDeleteMessage={(msgId, scope) => deleteMessage(msgId, currentConversation!, scope)}
            onClose={handleCloseChat}
            isOnline={onlineStatus[currentConversation] || false}
            onCameraRequest={() => setShowCamera(true)}
          />
        ) : currentConversation && !familyLoaded ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            <div className="circular-progress">
              <svg width="32" height="32" viewBox="0 0 40 40">
                <circle className="circular-progress-circle" cx="20" cy="20" r="18" fill="none" strokeWidth="4" />
              </svg>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', flexDirection: 'column', gap: '16px' }}>
            <span className="material-icons" style={{ fontSize: '64px', opacity: 0.3 }}>chat_bubble_outline</span>
            <div>Select a conversation to start messaging</div>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000,
            }}
            onClick={() => setShowNewChatModal(false)}
          />
          <div
            className="paper auth-form-container"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              padding: '24px',
              maxHeight: '70vh',
              overflowY: 'auto',
              minWidth: '300px',
              maxWidth: '500px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5em', color: 'white' }}>Start New Chat</h2>
              <button className="icon-btn" onClick={() => setShowNewChatModal(false)} style={{ color: 'white' }}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Search Field */}
            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="text"
                  placeholder="Search family members..."
                  value={newChatSearch}
                  onChange={(e) => setNewChatSearch(e.target.value)}
                />
                <div className="input-adornment input-adornment-end">
                  <span className="material-icons">search</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {familyMembers
                .filter(member => String(member.id) !== String(loginUser?.id))
                .filter(member => {
                  if (!newChatSearch) return true;
                  const searchLower = newChatSearch.toLowerCase();
                  const displayName = (member.displayName || member.display_name || '').toLowerCase();
                  const email = (member.email || '').toLowerCase();
                  const firstName = (member.firstName || member.first_name || '').toLowerCase();
                  const lastName = (member.lastName || member.last_name || '').toLowerCase();
                  return displayName.includes(searchLower) ||
                         email.includes(searchLower) ||
                         firstName.includes(searchLower) ||
                         lastName.includes(searchLower);
                })
                .map((member) => {
                  const displayName = member.displayName || member.display_name || member.email;
                  const avatarUrl = member.photoURL || member.photo_url;

                  return (
                    <div
                      key={member.id}
                      className="card"
                      onClick={() => {
                        handleSelectConversation(String(member.id));
                        setShowNewChatModal(false);
                      }}
                      style={{
                        cursor: 'pointer',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <div className="card-content" style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={displayName}
                              style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px rgba(255,255,255,0.7)' }}
                            />
                          ) : (
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              background: 'var(--primary-color)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '20px',
                              boxShadow: '0 0 0 2px rgba(255,255,255,0.7)'
                            }}>
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                              {displayName}
                            </div>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
    {showCamera && (
      <InAppCamera
        onCapture={handleCameraCapture}
        onClose={() => setShowCamera(false)}
      />
    )}
    </>
  );
};
